import { useCallback } from 'react';

import { api } from '../lib/api-client';
import { resetSessionCache } from '../lib/query-client';
import { useAppearanceStore } from '../stores/appearance-store';
import { useAuthStore, type AuthState } from '../stores/auth-store';

/**
 * Hook útil para componentes consumirem identidade + ações auth.
 *
 * Mantém estado em Zustand — sem efeito de hidratação local (api/me
 * é chamado pelo `RequireAuth` quando há token e ainda não há user).
 */
export function useAuth(): {
  user: AuthState['user'];
  tenant: AuthState['tenant'];
  capabilities: string[];
  isAuthenticated: boolean;
  logout: () => Promise<void>;
} {
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const capabilities = useAuthStore((s) => s.capabilities);
  const accessToken = useAuthStore((s) => s.accessToken);
  const clear = useAuthStore((s) => s.clear);

  const logout = useCallback(async () => {
    try {
      if (accessToken) await api.logout();
    } catch {
      // logout best-effort — falha silenciosa
    }
    clear();
    // Transição de identidade: descarta o server-state cacheado do usuário que
    // está saindo, para o próximo login não herdar dados dele (SPA não recarrega).
    resetSessionCache();
    // A aparência é preferência PESSOAL e segue a mesma regra: num terminal
    // compartilhado de operação, quem entra no turno seguinte não pode herdar o
    // tema de quem saiu. A escolha de quem saiu não se perde — está no perfil
    // dele no servidor, e volta no próximo login dele.
    useAppearanceStore.getState().reset();
  }, [accessToken, clear]);

  return {
    user,
    tenant,
    capabilities,
    isAuthenticated: accessToken !== null && user !== null,
    logout,
  };
}
